// Shared formatting + status-color helpers for the agent-cost dashboard and its
// detail views. Consolidated here so the summary page, charts, detail page, and
// side panels share one source of truth instead of drifting copies.
import { formatNumber } from 'utils/number';
import { parseLocalDateTime } from 'utils/dates';
import type { AgentCostSummary, UnpricedModel } from './types';

// Full local date + time of the snapshot, shown as the tile's "as of" stamp and
// its explanatory tooltip.
export const formatSnapshotAt = (iso: string | null | undefined): string =>
  parseLocalDateTime(iso ?? undefined, true);

// Shared copy for the unpriced-spend warnings so the banner, badge tooltip, and
// any future surface can't drift. `runCount` picks the subject ("3 run(s)" vs
// "Some runs"); `understates` appends the Total-Spend caveat.
export const unpricedMessage = ({
  models,
  runCount,
  understates = false,
}: {
  models: UnpricedModel[];
  runCount?: number;
  understates?: boolean;
}): string => {
  const names = models.map((m) => m.model).join(', ');
  const subject =
    runCount === undefined
      ? 'Some runs'
      : `${formatNumber({
          value: runCount,
          minPrecision: 0,
          includeCommas: true,
        })} run(s)`;
  const list = names
    ? `models not in the price list (${names}); their cost is shown as $0`
    : 'unpriced models, shown as $0';
  const caveat = understates ? ', so this total understates real spend' : '';
  return `${subject} used ${list}${caveat}.`;
};

// Currency formatter. Headline totals read cleaner at 2 decimals; per-run costs
// are tiny, so the run table/panel widen to 6. Parameterized so a single
// function covers every precision the callers need.
export const formatCost = (value: number, maxPrecision = 2): string =>
  formatNumber({
    value,
    minPrecision: 2,
    maxPrecision,
    style: 'currency',
    includeCommas: true,
  });

// Per-run costs are tiny, so the run table/panel widen to 6 decimals. Shared by
// the detail page's Cost column and the run-detail side panel so they can't drift.
export const formatRunCost = (value: number): string => formatCost(value, 6);

// Human-readable duration (s / m s / h m s). Re-exported from utils so the dashboard's
// existing imports keep working; the implementation is shared with the well-spacing
// job-status page and the Eval 3 run spinner, which this used to be duplicated against.
export { formatDuration } from 'utils/duration';

export const titleize = (value: string): string =>
  value.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

// Single source of truth for run/step status colors. The Carbon tag color and
// the chart hex are derived from the same map so they can't drift apart.
type CarbonTagColor = 'green' | 'red' | 'blue' | 'gray';

interface StatusStyle {
  tag: CarbonTagColor;
  hex: string;
}

// `blocked`/`skipped` only occur on workflow steps; harmless to include
// everywhere so the one helper covers every caller.
const STATUS_STYLES: Record<string, StatusStyle> = {
  processed: { tag: 'green', hex: '#24a148' },
  failed: { tag: 'red', hex: '#da1e28' },
  processing: { tag: 'blue', hex: '#0f62fe' },
  pending: { tag: 'gray', hex: '#8d8d8d' },
  blocked: { tag: 'gray', hex: '#8d8d8d' },
  skipped: { tag: 'gray', hex: '#8d8d8d' },
};

// Carbon `Tag` color for a status, defaulting to gray for anything unknown.
export const statusTagColor = (status: string): CarbonTagColor =>
  STATUS_STYLES[status]?.tag ?? 'gray';

// Hex color for a status (chart series). Returns undefined for unknown statuses
// so the chart falls back to its themed palette.
export const statusHex = (status: string): string | undefined =>
  STATUS_STYLES[status]?.hex;

// Run-count invariant for the summary table + KPI strip: a run is "in progress"
// (pending/processing) when it's counted in Total Runs but hasn't completed
// (succeeded or failed). Kept here as the single source of truth so the table
// column and the In Progress KPI can't drift, and clamped at 0 so a transient
// over-count never renders negative.
type RunCounts = Pick<
  AgentCostSummary,
  'totalRuns' | 'totalSuccess' | 'totalFailed'
>;

export const inProgressCount = (summary: RunCounts): number =>
  Math.max(0, summary.totalRuns - summary.totalSuccess - summary.totalFailed);

// Success rate over *completed* runs (succeeded ÷ (succeeded + failed)).
// In-progress runs are excluded from the denominator so they don't deflate the
// rate; returns 0 when nothing has completed yet.
export const successRate = (summary: RunCounts): number => {
  const completed = summary.totalSuccess + summary.totalFailed;
  return completed > 0 ? (summary.totalSuccess / completed) * 100 : 0;
};
